import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { 
  ArrowLeft, Plus, Trash2, CheckCircle, XCircle, Loader2, 
  Link2, Edit2, Bell, BellRing, MessageSquare, Smartphone,
  ChevronDown, ChevronUp, Settings2
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Alert type labels for UI
const ALERT_LABELS = {
  stop_loss: { label: "Stop Loss Hit", icon: "🔴", description: "When price hits stop loss level" },
  target_reached: { label: "Target Reached", icon: "🟢", description: "When price hits target level" },
  entry_signal: { label: "Entry Signal", icon: "🔵", description: "New trade signal generated" },
  trade_not_taken: { label: "Trade Not Taken", icon: "⚠️", description: "Signal not executed warning" },
  daily_summary: { label: "Daily Summary", icon: "📊", description: "End of day trading report" },
  excel_upload: { label: "Excel Upload", icon: "✅", description: "Upload confirmation" }
};

export const Accounts = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [testingConnection, setTestingConnection] = useState(null);
  const [testingTelegram, setTestingTelegram] = useState(null);
  const [testingPush, setTestingPush] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedNotifications, setExpandedNotifications] = useState({});

  const [formData, setFormData] = useState({
    name: "",
    broker: "AngelOne",
    description: "",
    api_key: "",
    client_id: "",
    pin: "",
    totp_secret: "",
    speedbot_email: "",
    speedbot_password: ""
  });

  const [notificationForm, setNotificationForm] = useState({
    telegram_enabled: false,
    telegram_bot_token: "",
    telegram_chat_id: "",
    push_enabled: false,
    alert_routing: {}
  });

  const [vapidPublicKey, setVapidPublicKey] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    fetchAccounts();
    fetchVapidKey();
    // Check if running as standalone PWA
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
    setIsStandalone(standalone);
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await api.get("/accounts");
      setAccounts(response.data);
    } catch (error) {
      console.error("Error fetching accounts:", error);
      toast.error("Failed to load accounts");
    } finally {
      setLoading(false);
    }
  };

  const fetchVapidKey = async () => {
    try {
      const response = await api.get("/push/vapid-key");
      if (response.data.status) {
        setVapidPublicKey(response.data.publicKey);
      }
    } catch (error) {
      console.error("Error fetching VAPID key:", error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      broker: "AngelOne",
      description: "",
      api_key: "",
      client_id: "",
      pin: "",
      totp_secret: "",
      speedbot_email: "",
      speedbot_password: ""
    });
  };

  const handleAddAccount = async () => {
    if (!formData.name) {
      toast.error("Account name is required");
      return;
    }

    setSaving(true);
    try {
      await api.post("/accounts", formData);
      toast.success("Account created successfully");
      setShowAddModal(false);
      resetForm();
      fetchAccounts();
    } catch (error) {
      console.error("Error creating account:", error);
      toast.error("Failed to create account");
    } finally {
      setSaving(false);
    }
  };

  const handleEditAccount = async () => {
    if (!formData.name) {
      toast.error("Account name is required");
      return;
    }

    setSaving(true);
    try {
      await api.put(`/accounts/${editingAccount.id}`, formData);
      toast.success("Account updated successfully");
      setShowEditModal(false);
      setEditingAccount(null);
      resetForm();
      fetchAccounts();
    } catch (error) {
      console.error("Error updating account:", error);
      toast.error("Failed to update account");
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (account) => {
    setEditingAccount(account);
    const creds = account.broker_credentials || {};
    const speedbotCreds = account.speedbot_credentials || {};
    let brokerValue = account.broker || "AngelOne";
    if (brokerValue === "Angel One") brokerValue = "AngelOne";
    setFormData({
      name: account.name || "",
      broker: brokerValue,
      description: account.description || "",
      api_key: creds.api_key || "",
      client_id: creds.client_id || "",
      pin: "",
      totp_secret: "",
      speedbot_email: speedbotCreds.email || "",
      speedbot_password: ""
    });
    setShowEditModal(true);
  };

  const openNotificationModal = async (account) => {
    setEditingAccount(account);
    try {
      const response = await api.get(`/accounts/${account.id}/notifications`);
      if (response.data.status) {
        const settings = response.data.data;
        setNotificationForm({
          telegram_enabled: settings.telegram?.enabled || false,
          telegram_bot_token: settings.telegram?.bot_token || "",
          telegram_chat_id: settings.telegram?.chat_id || "",
          telegram_is_verified: settings.telegram?.is_verified || false,
          push_enabled: settings.push_enabled || false,
          push_subscriptions_count: settings.push_subscriptions?.length || 0,
          alert_routing: settings.alert_routing || {}
        });
        if (response.data.vapid_public_key) {
          setVapidPublicKey(response.data.vapid_public_key);
        }
      }
    } catch (error) {
      console.error("Error fetching notification settings:", error);
      toast.error("Failed to load notification settings");
    }
    setShowNotificationModal(true);
  };

  const handleDeleteAccount = async () => {
    if (!accountToDelete) return;

    try {
      await api.delete(`/accounts/${accountToDelete}`);
      toast.success("Account deleted successfully");
      fetchAccounts();
    } catch (error) {
      console.error("Error deleting account:", error);
      toast.error("Failed to delete account");
    } finally {
      setDeleteDialogOpen(false);
      setAccountToDelete(null);
    }
  };

  const handleTestConnection = async (accountId) => {
    setTestingConnection(accountId);
    try {
      const response = await api.post(`/accounts/${accountId}/test-connection`);
      if (response.data.status) {
        toast.success(response.data.message);
        fetchAccounts();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error("Error testing connection:", error);
      toast.error("Connection test failed");
    } finally {
      setTestingConnection(null);
    }
  };

  const handleSaveNotifications = async () => {
    if (!editingAccount) return;

    setSaving(true);
    try {
      await api.put(`/accounts/${editingAccount.id}/notifications`, {
        telegram_enabled: notificationForm.telegram_enabled,
        telegram_bot_token: notificationForm.telegram_bot_token,
        telegram_chat_id: notificationForm.telegram_chat_id,
        push_enabled: notificationForm.push_enabled,
        alert_routing: notificationForm.alert_routing
      });
      toast.success("Notification settings saved");
      setShowNotificationModal(false);
      fetchAccounts();
    } catch (error) {
      console.error("Error saving notifications:", error);
      toast.error("Failed to save notification settings");
    } finally {
      setSaving(false);
    }
  };

  const handleTestTelegram = async () => {
    if (!editingAccount) return;

    // Save first if there are changes
    if (notificationForm.telegram_bot_token && notificationForm.telegram_chat_id) {
      await api.put(`/accounts/${editingAccount.id}/notifications`, {
        telegram_enabled: notificationForm.telegram_enabled,
        telegram_bot_token: notificationForm.telegram_bot_token,
        telegram_chat_id: notificationForm.telegram_chat_id
      });
    }

    setTestingTelegram(editingAccount.id);
    try {
      const response = await api.post(`/accounts/${editingAccount.id}/notifications/test-telegram`);
      if (response.data.status) {
        toast.success(response.data.message);
        setNotificationForm(prev => ({ ...prev, telegram_is_verified: true }));
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error("Error testing Telegram:", error);
      toast.error("Telegram test failed");
    } finally {
      setTestingTelegram(null);
    }
  };

  // Web Push subscription
  const urlBase64ToUint8Array = useCallback((base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }, []);

  const handleEnablePush = async () => {
    if (!editingAccount) {
      toast.error("No account selected");
      return;
    }

    try {
      // Check if running as PWA (standalone mode)
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                          window.navigator.standalone === true;
      
      if (!isStandalone) {
        toast.error("Please add this app to your Home Screen first, then try again from the installed app.", {
          duration: 6000
        });
        return;
      }

      // Check if push is supported
      if (!('serviceWorker' in navigator)) {
        toast.error("Service Workers not supported in this browser");
        return;
      }
      
      if (!('PushManager' in window)) {
        toast.error("Push notifications not supported. Requires iOS 16.4+ or modern Android.");
        return;
      }

      if (!('Notification' in window)) {
        toast.error("Notifications API not available");
        return;
      }

      // Fetch VAPID key if not available
      let publicKey = vapidPublicKey;
      if (!publicKey) {
        try {
          const keyResponse = await api.get("/push/vapid-key");
          if (keyResponse.data.status && keyResponse.data.publicKey) {
            publicKey = keyResponse.data.publicKey;
            setVapidPublicKey(publicKey);
          } else {
            toast.error("Could not fetch push configuration");
            return;
          }
        } catch (e) {
          toast.error("Could not fetch push configuration from server");
          return;
        }
      }

      // Request permission
      console.log("Requesting notification permission...");
      const permission = await Notification.requestPermission();
      console.log("Permission result:", permission);
      
      if (permission !== 'granted') {
        toast.error(`Notification permission ${permission}. Please enable in Settings.`);
        return;
      }

      // Get service worker registration
      console.log("Getting service worker registration...");
      const registration = await navigator.serviceWorker.ready;
      console.log("Service worker ready:", registration);

      // Subscribe to push
      console.log("Subscribing to push with VAPID key:", publicKey.substring(0, 20) + "...");
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });
      console.log("Subscription created:", subscription.endpoint.substring(0, 50) + "...");

      // Send subscription to backend
      const subJson = subscription.toJSON();
      await api.post(`/accounts/${editingAccount.id}/notifications/push-subscribe`, {
        endpoint: subJson.endpoint,
        keys: subJson.keys
      });

      setNotificationForm(prev => ({ 
        ...prev, 
        push_enabled: true,
        push_subscriptions_count: (prev.push_subscriptions_count || 0) + 1
      }));
      
      toast.success("Push notifications enabled!");
    } catch (error) {
      console.error("Error enabling push:", error);
      
      // Provide more specific error messages
      if (error.name === 'NotAllowedError') {
        toast.error("Permission denied. Enable notifications in your device Settings.");
      } else if (error.name === 'AbortError') {
        toast.error("Push subscription was aborted. Please try again.");
      } else if (error.message?.includes('applicationServerKey')) {
        toast.error("Invalid push configuration. Please contact support.");
      } else {
        toast.error(`Failed to enable push: ${error.message || 'Unknown error'}`);
      }
    }
  };

  const handleTestPush = async () => {
    if (!editingAccount) return;

    setTestingPush(editingAccount.id);
    try {
      const response = await api.post(`/accounts/${editingAccount.id}/notifications/test-push`);
      if (response.data.status) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error("Error testing push:", error);
      toast.error("Push test failed");
    } finally {
      setTestingPush(null);
    }
  };

  const handleAlertRoutingChange = (alertType, channel, enabled) => {
    setNotificationForm(prev => ({
      ...prev,
      alert_routing: {
        ...prev.alert_routing,
        [alertType]: {
          ...(prev.alert_routing[alertType] || {}),
          [channel]: enabled
        }
      }
    }));
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const renderAccountForm = () => (
    <div className="space-y-4">
      {/* Account Name */}
      <div className="space-y-2">
        <Label className="text-slate-300 text-sm font-medium">Account Name *</Label>
        <Input
          value={formData.name}
          onChange={(e) => handleChange("name", e.target.value)}
          className="bg-black/20 border-white/10 text-white"
          placeholder="e.g., ASHOK-AO, Personal Trading"
          data-testid="account-name-input"
        />
      </div>

      {/* Broker Selection */}
      <div className="space-y-2">
        <Label className="text-slate-300 text-sm font-medium">Broker</Label>
        <Select value={formData.broker} onValueChange={(value) => handleChange("broker", value)}>
          <SelectTrigger className="bg-black/20 border-white/10 text-white">
            <SelectValue placeholder="Select Broker" />
          </SelectTrigger>
          <SelectContent className="bg-charcoal border-white/10">
            <SelectItem value="AngelOne">AngelOne</SelectItem>
            <SelectItem value="Zerodha">Zerodha (Coming Soon)</SelectItem>
            <SelectItem value="Other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label className="text-slate-300 text-sm font-medium">Description</Label>
        <Textarea
          value={formData.description}
          onChange={(e) => handleChange("description", e.target.value)}
          className="bg-black/20 border-white/10 text-white"
          placeholder="Optional description for this account"
          rows={2}
        />
      </div>

      {/* Broker Credentials Section */}
      {formData.broker === "AngelOne" && (
        <div className="border-t border-white/10 pt-4 mt-4">
          <h4 className="text-white font-medium mb-3 flex items-center gap-2">
            <Link2 className="w-4 h-4 text-blue-400" />
            AngelOne API Credentials
          </h4>
          <p className="text-xs text-slate-500 mb-4">
            Link this account to your AngelOne broker for importing positions and live prices.
          </p>

          <div className="grid grid-cols-2 gap-4">
            {/* API Key */}
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">API Key</Label>
              <Input
                value={formData.api_key}
                onChange={(e) => handleChange("api_key", e.target.value)}
                className="bg-black/20 border-white/10 text-white font-mono text-sm"
                placeholder="API Key"
                data-testid="account-api-key-input"
              />
            </div>

            {/* Client ID */}
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Client ID</Label>
              <Input
                value={formData.client_id}
                onChange={(e) => handleChange("client_id", e.target.value)}
                className="bg-black/20 border-white/10 text-white font-mono text-sm"
                placeholder="e.g., A12345678"
                data-testid="account-client-id-input"
              />
            </div>

            {/* PIN */}
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">PIN (4 digits)</Label>
              <Input
                type="password"
                maxLength={4}
                value={formData.pin}
                onChange={(e) => handleChange("pin", e.target.value)}
                className="bg-black/20 border-white/10 text-white font-mono text-sm"
                placeholder={editingAccount ? "Leave empty to keep existing" : "4-digit PIN"}
                data-testid="account-pin-input"
              />
            </div>

            {/* TOTP Secret */}
            <div className="space-y-2 col-span-2">
              <Label className="text-slate-300 text-sm">TOTP Secret (Base32)</Label>
              <Input
                type="password"
                value={formData.totp_secret}
                onChange={(e) => handleChange("totp_secret", e.target.value.toUpperCase().replace(/[^A-Z2-7]/g, ''))}
                className="bg-black/20 border-white/10 text-white font-mono text-sm"
                placeholder={editingAccount ? "Leave empty to keep existing" : "e.g., 2BR4BKX7IAKMHNX6..."}
                data-testid="account-totp-input"
              />
              <p className="text-xs text-yellow-500">
                Must be a Base32 string (A-Z, 2-7). Found in AngelOne SmartAPI → Enable TOTP → Copy secret key
              </p>
            </div>
          </div>

          <p className="text-xs text-slate-500 mt-3">
            Get your credentials from <a href="https://smartapi.angelone.in" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">smartapi.angelone.in</a>
          </p>
        </div>
      )}

      {/* SpeedBot Credentials Section */}
      <div className="bg-black/20 rounded-lg p-4 border border-white/10">
        <h4 className="text-white font-medium mb-3 flex items-center gap-2">
          <span className="text-yellow-400">⚡</span> SpeedBot Credentials
          <span className="text-xs text-slate-500 font-normal">(for Fetch Signals)</span>
        </h4>
        
        <div className="grid grid-cols-2 gap-3">
          {/* SpeedBot Email */}
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm">Email / Phone</Label>
            <Input
              type="text"
              value={formData.speedbot_email}
              onChange={(e) => handleChange("speedbot_email", e.target.value)}
              className="bg-black/20 border-white/10 text-white text-sm"
              placeholder="email@example.com or phone"
              data-testid="speedbot-email-input"
            />
          </div>

          {/* SpeedBot Password */}
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm">Password</Label>
            <Input
              type="password"
              value={formData.speedbot_password}
              onChange={(e) => handleChange("speedbot_password", e.target.value)}
              className="bg-black/20 border-white/10 text-white text-sm"
              placeholder={editingAccount ? "Leave empty to keep existing" : "SpeedBot password"}
              data-testid="speedbot-password-input"
            />
          </div>
        </div>

        <p className="text-xs text-slate-500 mt-3">
          Login credentials for <a href="https://aasma-trade.speedbot.tech" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">SpeedBot platform</a> to fetch Order Sheet signals
        </p>
      </div>
    </div>
  );

  const renderNotificationForm = () => (
    <div className="space-y-6">
      {/* Telegram Settings */}
      <div className="bg-black/20 rounded-lg p-4 border border-white/10">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <MessageSquare className="w-5 h-5 text-blue-400" />
            <div>
              <h4 className="text-white font-medium">Telegram</h4>
              <p className="text-xs text-slate-500">Receive alerts via Telegram bot</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {notificationForm.telegram_is_verified && (
              <span className="text-xs text-profit flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> Verified
              </span>
            )}
            <Switch
              checked={notificationForm.telegram_enabled}
              onCheckedChange={(checked) => setNotificationForm(prev => ({ ...prev, telegram_enabled: checked }))}
            />
          </div>
        </div>

        {notificationForm.telegram_enabled && (
          <div className="space-y-3 mt-4 pt-4 border-t border-white/5">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-slate-400 text-xs">Bot Token</Label>
                <Input
                  value={notificationForm.telegram_bot_token}
                  onChange={(e) => setNotificationForm(prev => ({ ...prev, telegram_bot_token: e.target.value, telegram_is_verified: false }))}
                  className="bg-black/30 border-white/10 text-white font-mono text-sm"
                  placeholder="123456789:ABCdef..."
                  data-testid="telegram-bot-token-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-400 text-xs">Chat ID</Label>
                <Input
                  value={notificationForm.telegram_chat_id}
                  onChange={(e) => setNotificationForm(prev => ({ ...prev, telegram_chat_id: e.target.value, telegram_is_verified: false }))}
                  className="bg-black/30 border-white/10 text-white font-mono text-sm"
                  placeholder="e.g., 1234567890"
                  data-testid="telegram-chat-id-input"
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500">
                Create a bot via <a href="https://t.me/BotFather" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">@BotFather</a> and get chat ID from <a href="https://t.me/userinfobot" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">@userinfobot</a>
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={handleTestTelegram}
                disabled={testingTelegram || !notificationForm.telegram_bot_token || !notificationForm.telegram_chat_id}
                className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                data-testid="test-telegram-button"
              >
                {testingTelegram ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Bell className="w-3 h-3 mr-1" />}
                Test
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Push Notifications */}
      <div className="bg-black/20 rounded-lg p-4 border border-white/10">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Smartphone className="w-5 h-5 text-green-400" />
            <div>
              <h4 className="text-white font-medium">Push Notifications</h4>
              <p className="text-xs text-slate-500">Native alerts on iOS/Android PWA</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {notificationForm.push_subscriptions_count > 0 && (
              <span className="text-xs text-profit flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> {notificationForm.push_subscriptions_count} device(s)
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {!isStandalone ? (
            <div className="w-full">
              <p className="text-xs text-yellow-500 mb-2">
                ⚠️ Push notifications require the app to be installed. Add this app to your Home Screen first:
              </p>
              <p className="text-xs text-slate-500">
                iOS: Tap Share → &quot;Add to Home Screen&quot; → Open from Home Screen
              </p>
            </div>
          ) : notificationForm.push_subscriptions_count === 0 ? (
            <Button
              size="sm"
              onClick={handleEnablePush}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="enable-push-button"
            >
              <BellRing className="w-3 h-3 mr-1" />
              Enable Push on This Device
            </Button>
          ) : (
            <>
              <Button
                size="sm"
                onClick={handleEnablePush}
                variant="outline"
                className="border-green-500/30 text-green-400 hover:bg-green-500/10"
              >
                <Plus className="w-3 h-3 mr-1" />
                Add This Device
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleTestPush}
                disabled={testingPush}
                className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                data-testid="test-push-button"
              >
                {testingPush ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Bell className="w-3 h-3 mr-1" />}
                Test
              </Button>
            </>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Requires iOS 16.4+ or Android with app added to Home Screen
        </p>
      </div>

      {/* Alert Routing */}
      <div className="bg-black/20 rounded-lg p-4 border border-white/10">
        <h4 className="text-white font-medium mb-4 flex items-center gap-2">
          <Settings2 className="w-4 h-4 text-purple-400" />
          Alert Routing
        </h4>
        <p className="text-xs text-slate-500 mb-4">Configure which alerts go to which channel</p>

        <div className="space-y-2">
          {Object.entries(ALERT_LABELS).map(([alertType, { label, icon, description }]) => {
            const routing = notificationForm.alert_routing[alertType] || { telegram: true, push: true };
            return (
              <div key={alertType} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{icon}</span>
                  <div>
                    <span className="text-sm text-white">{label}</span>
                    <p className="text-xs text-slate-600">{description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <Switch
                      checked={routing.telegram}
                      onCheckedChange={(checked) => handleAlertRoutingChange(alertType, "telegram", checked)}
                      className="scale-75"
                    />
                    <span className="text-xs text-slate-400">Telegram</span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <Switch
                      checked={routing.push}
                      onCheckedChange={(checked) => handleAlertRoutingChange(alertType, "push", checked)}
                      className="scale-75"
                    />
                    <span className="text-xs text-slate-400">Push</span>
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="accounts-page">
      <button
        onClick={() => navigate("/settings")}
        className="flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Settings
      </button>

      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="font-heading font-semibold tracking-tight text-4xl sm:text-5xl text-white mb-2">
            Trading Accounts
          </h1>
          <p className="text-slate-400 text-base">Manage your broker accounts, API credentials, and notifications</p>
        </div>
        <Button
          onClick={() => { resetForm(); setShowAddModal(true); }}
          className="bg-white text-black hover:bg-slate-200 flex items-center gap-2"
          data-testid="add-account-button"
        >
          <Plus className="w-4 h-4" />
          Add Account
        </Button>
      </div>

      {/* Accounts List */}
      <div className="grid gap-4">
        {accounts.length === 0 ? (
          <div className="bg-charcoal/50 border border-white/10 rounded-md p-12 text-center">
            <p className="text-slate-400 mb-4">No trading accounts configured yet.</p>
            <Button
              onClick={() => { resetForm(); setShowAddModal(true); }}
              className="bg-white text-black hover:bg-slate-200"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Account
            </Button>
          </div>
        ) : (
          accounts.map((account) => {
            const creds = account.broker_credentials || {};
            const isConnected = creds.is_connected;
            const hasCredentials = creds.api_key && creds.client_id;
            const notifSettings = account.notification_settings || {};
            const hasTelegram = notifSettings.telegram?.enabled;
            const hasPush = notifSettings.push_enabled;

            return (
              <div
                key={account.id}
                className="bg-charcoal/50 border border-white/10 rounded-md p-6"
                data-testid={`account-card-${account.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-white text-xl font-semibold">{account.name}</h3>
                      {account.broker && (
                        <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded">
                          {account.broker}
                        </span>
                      )}
                      {isConnected && (
                        <span className="px-2 py-0.5 bg-profit/20 text-profit text-xs rounded flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          Connected
                        </span>
                      )}
                    </div>
                    
                    {account.description && (
                      <p className="text-slate-400 text-sm mb-3">{account.description}</p>
                    )}

                    {/* Credentials & Notifications Status */}
                    <div className="flex items-center gap-4 text-sm flex-wrap">
                      {hasCredentials ? (
                        <>
                          <span className="text-slate-500">Client ID: <span className="text-slate-300 font-mono">{creds.client_id}</span></span>
                          <span className="text-slate-500">API Key: <span className="text-slate-300 font-mono">{creds.api_key?.substring(0, 4)}...</span></span>
                        </>
                      ) : (
                        <span className="text-yellow-500 text-sm flex items-center gap-1">
                          <XCircle className="w-3 h-3" />
                          No broker credentials
                        </span>
                      )}
                      
                      {/* Notification indicators */}
                      <span className="text-slate-600">|</span>
                      <div className="flex items-center gap-2">
                        {hasTelegram && (
                          <span className="px-2 py-0.5 bg-blue-500/10 text-blue-400 text-xs rounded flex items-center gap-1">
                            <MessageSquare className="w-3 h-3" />
                            Telegram
                          </span>
                        )}
                        {hasPush && (
                          <span className="px-2 py-0.5 bg-green-500/10 text-green-400 text-xs rounded flex items-center gap-1">
                            <Smartphone className="w-3 h-3" />
                            Push
                          </span>
                        )}
                        {!hasTelegram && !hasPush && (
                          <span className="text-slate-600 text-xs">No notifications</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {hasCredentials && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTestConnection(account.id)}
                        disabled={testingConnection === account.id}
                        className="border-white/10 text-slate-300 hover:text-white hover:bg-white/10"
                        data-testid={`test-connection-${account.id}`}
                      >
                        {testingConnection === account.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Link2 className="w-4 h-4 mr-1" />
                            Test
                          </>
                        )}
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openNotificationModal(account)}
                      className="border-white/10 text-slate-300 hover:text-white hover:bg-white/10"
                      data-testid={`notifications-${account.id}`}
                    >
                      <Bell className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditModal(account)}
                      className="border-white/10 text-slate-300 hover:text-white hover:bg-white/10"
                      data-testid={`edit-account-${account.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setAccountToDelete(account.id); setDeleteDialogOpen(true); }}
                      className="border-white/10 text-loss hover:text-loss hover:bg-loss/10"
                      data-testid={`delete-account-${account.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Add Account Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-charcoal border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">Add Trading Account</DialogTitle>
            <DialogDescription className="text-slate-400">
              Create a new trading account and link it to your broker
            </DialogDescription>
          </DialogHeader>
          
          {renderAccountForm()}

          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowAddModal(false)} className="border-white/10 text-slate-300">
              Cancel
            </Button>
            <Button onClick={handleAddAccount} disabled={saving} className="bg-white text-black hover:bg-slate-200">
              {saving ? "Creating..." : "Create Account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Account Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-charcoal border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">Edit Account</DialogTitle>
            <DialogDescription className="text-slate-400">
              Update account details and broker credentials
            </DialogDescription>
          </DialogHeader>
          
          {renderAccountForm()}

          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowEditModal(false)} className="border-white/10 text-slate-300">
              Cancel
            </Button>
            <Button onClick={handleEditAccount} disabled={saving} className="bg-white text-black hover:bg-slate-200">
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notification Settings Modal */}
      <Dialog open={showNotificationModal} onOpenChange={setShowNotificationModal}>
        <DialogContent className="bg-charcoal border-white/10 text-white max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold flex items-center gap-2">
              <Bell className="w-5 h-5 text-yellow-400" />
              Notification Settings
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Configure alerts for <span className="text-white font-medium">{editingAccount?.name}</span>
            </DialogDescription>
          </DialogHeader>
          
          {renderNotificationForm()}

          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowNotificationModal(false)} className="border-white/10 text-slate-300">
              Cancel
            </Button>
            <Button onClick={handleSaveNotifications} disabled={saving} className="bg-white text-black hover:bg-slate-200">
              {saving ? "Saving..." : "Save Settings"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-charcoal border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Account</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Are you sure you want to delete this account? This will not delete any trades associated with this account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/10 text-slate-300 hover:bg-white/5">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              className="bg-loss hover:bg-loss/80 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Accounts;
